import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import ServicePath from "../../../services/webservice";
import Swal from "sweetalert2";
import "./Music.css";

const AddMusic = () => {
  const audioRef = useRef(null);
  const navigate = useNavigate();

  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [showIconModal, setShowIconModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const MUSIC_ICONS = [
    "music_note", "audiotrack", "queue_music", "library_music", 
    "play_circle_filled", "play_arrow", "headset", "headset_mic", 
    "mic", "radio", "volume_up", "surround_sound", 
    "album", "graphic_eq", "speaker", "music_video",
    "featured_play_list", "playlist_play", "art_track", "library_add",
    "straighten", "waves", "spa", "self_improvement",
    "nightlight_round", "bedtime", "nature", "water_drop",
    "grass", "flare", "favorite", "healing",
    "ac_unit", "local_fire_department", "wb_sunny", "cloud",
    "brightness_4", "terrain", "eco", "air"
  ];

  const categories = [
    { value: "Music", label: "Music", color: "#3b82f6" },
    { value: "Sounds", label: "Sounds", color: "#10b981" },
    { value: "Stories", label: "Stories", color: "#f59e0b" },
    { value: "Meditation", label: "Meditation", color: "#8b5cf6" },
    { value: "Breathing", label: "Breathing", color: "#14b8a6" }
  ];

  const statuses = [
    { value: "A", label: "Active", color: "#10b981" },
    { value: "I", label: "Inactive", color: "#94a3b8" }
  ];

  const [formData, setFormData] = useState({
    title: "",
    artist: "",
    category: "Music",
    colorHex: "#8F94FB",
    icon: "",
    status: "A",
    duration: "",
    audioUrl: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCustomSelect = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsCategoryOpen(false);
    setIsStatusOpen(false);
  };

  const handleMusicUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (errors.audioUrl) {
      setErrors((prev) => ({ ...prev, audioUrl: "" }));
    }

    const audio = document.createElement("audio");
    audio.preload = "metadata";

    audio.onloadedmetadata = () => {
      window.URL.revokeObjectURL(audio.src);
      const minutes = Math.floor(audio.duration / 60);
      const seconds = Math.floor(audio.duration % 60);

      setFormData((prev) => ({
        ...prev,
        audioUrl: file,
        duration: `${minutes}:${seconds.toString().padStart(2, "0")}`,
      }));
    };

    audio.src = URL.createObjectURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let newErrors = {};
    if (!formData.title.trim()) newErrors.title = "This field is mandatory to fill";
    if (!formData.artist.trim()) newErrors.artist = "This field is mandatory to fill";
    if (!formData.icon.trim()) newErrors.icon = "This field is mandatory to fill";
    if (!formData.audioUrl) newErrors.audioUrl = "Audio file is mandatory to upload";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    const data = new FormData();
    data.append("audioFile", formData.audioUrl);
    
    const payload = {
        title: formData.title.replace(/\b\w/g, char => char.toUpperCase()),
        artist: formData.artist.replace(/\b\w/g, char => char.toUpperCase()),
        category: formData.category,
        colorHex: formData.colorHex,
        icon: formData.icon,
        status: formData.status,
        duration: formData.duration,
    };
    data.append("data", JSON.stringify(payload));

    try {
        const response = await fetch(`${ServicePath}api/addMusic`, {
            method: "POST",
            body: data
        });
        const result = await response.json();
        
        if (result.response === 'S') {
            Swal.fire('Success', 'Music added successfully!', 'success').then(() => {
                navigate('/music-listing');
            });
        } else {
            Swal.fire('Error', 'Error adding music: ' + result.message, 'error');
        }
    } catch (error) {
        console.error("Error submitting form", error);
        Swal.fire('Error', 'Error adding music', 'error');
    } finally {
        setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: "",
      artist: "",
      category: "Music",
      colorHex: "#8F94FB",
      icon: "",
      status: "A",
      duration: "",
      audioUrl: null,
    });
    if (audioRef.current) {
      audioRef.current.value = "";
    }
    setErrors({});
  };

  return (
    <div className="music-manage-container">
      <form className="music-form" onSubmit={handleSubmit}>
        <div className="full-width" style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
          <button 
            type="button" 
            onClick={() => navigate('/music-listing')} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#1e3a8a', padding: 0 }}
            title="Go Back"
          >
            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="24px" width="24px" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 11H6.414l5.293-5.293-1.414-1.414L2.586 12l7.707 7.707 1.414-1.414L6.414 13H21z"></path>
            </svg>
          </button>
          <h2 className="page-title" style={{ margin: 0 }}>Add Music</h2>
        </div>

        <div className="form-group">
          <label>Title <span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            name="title"
            placeholder="e.g. Melodic Sunset"
            className="form-input capitalize-input"
            value={formData.title}
            onChange={handleChange}
          />
          {errors.title && <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.title}</span>}
        </div>

        <div className="form-group">
          <label>Artist <span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            name="artist"
            placeholder="e.g. Ambient Rest"
            className="form-input capitalize-input"
            value={formData.artist}
            onChange={handleChange}
          />
          {errors.artist && <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.artist}</span>}
        </div>

        <div className="form-group">
          <label>Category</label>
          <div className="custom-dropdown-container" tabIndex={0} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsCategoryOpen(false) }}>
            <div className={`custom-dropdown-header ${isCategoryOpen ? 'open' : ''}`} onClick={() => setIsCategoryOpen(!isCategoryOpen)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {formData.category ? (
                  <>
                    <span className="status-dot" style={{ backgroundColor: categories.find(c => c.value === formData.category)?.color || '#ccc' }}></span>
                    {categories.find(c => c.value === formData.category)?.label}
                  </>
                ) : "Select Category"}
              </div>
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M16.293 9.293 12 13.586 7.707 9.293l-1.414 1.414L12 16.414l5.707-5.707z"></path></svg>
            </div>
            {isCategoryOpen && (
              <div className="custom-dropdown-list">
                {categories.map((cat) => (
                  <div key={cat.value} className={`custom-dropdown-item ${formData.category === cat.value ? 'selected' : ''}`} onClick={() => handleCustomSelect('category', cat.value)}>
                    <span className="status-dot" style={{ backgroundColor: cat.color }}></span>
                    {cat.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Theme Color (Hex)</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="color"
              name="colorHex"
              className="color-picker-circle"
              style={{ width: '45px', height: '45px', padding: '0', border: 'none', cursor: 'pointer', outline: 'none' }}
              value={formData.colorHex}
              onChange={handleChange}
            />
            <input
              type="text"
              name="colorHex"
              className="form-input"
              value={formData.colorHex}
              onChange={handleChange}
              placeholder="#8F94FB"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Icon Identifier <span style={{ color: 'red' }}>*</span></label>
          <div 
             className="form-input" 
             style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: '#fff' }}
             onClick={() => setShowIconModal(true)}
          >
             <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                 {formData.icon ? (
                     <>
                        <i className="material-icons" style={{ fontSize: '20px', color: '#6366f1' }}>{formData.icon}</i>
                        <span>{formData.icon.replace(/_/g, ' ')}</span>
                     </>
                 ) : (
                     <span style={{ color: '#94a3b8' }}>Select an Icon...</span>
                 )}
             </div>
             <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M16.293 9.293 12 13.586 7.707 9.293l-1.414 1.414L12 16.414l5.707-5.707z"></path></svg>
          </div>
          {errors.icon && <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.icon}</span>}
        </div>

        <div className="form-group">
          <label>Status</label>
          <div className="custom-dropdown-container" tabIndex={0} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsStatusOpen(false) }}>
            <div className={`custom-dropdown-header ${isStatusOpen ? 'open' : ''}`} onClick={() => setIsStatusOpen(!isStatusOpen)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className="status-dot" style={{ backgroundColor: statuses.find(s => s.value === formData.status)?.color || '#ccc' }}></span>
                {statuses.find(s => s.value === formData.status)?.label}
              </div>
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M16.293 9.293 12 13.586 7.707 9.293l-1.414 1.414L12 16.414l5.707-5.707z"></path></svg>
            </div>
            {isStatusOpen && (
              <div className="custom-dropdown-list">
                {statuses.map((stat) => (
                  <div key={stat.value} className={`custom-dropdown-item ${formData.status === stat.value ? 'selected' : ''}`} onClick={() => handleCustomSelect('status', stat.value)}>
                    <span className="status-dot" style={{ backgroundColor: stat.color }}></span>
                    {stat.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group full-width">
          {!formData.audioUrl ? (
            <>
              <label>Upload Audio File <span style={{ color: 'red' }}>*</span></label>
              <div 
                 className={`drag-drop-zone ${errors.audioUrl ? 'error-state' : ''}`}
                 onClick={() => audioRef.current && audioRef.current.click()}
                 onDragOver={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                 }}
                 onDrop={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   const file = e.dataTransfer.files[0];
                   if (file && file.type.startsWith('audio/')) {
                     handleMusicUpload({ target: { files: [file] } });
                   } else {
                     Swal.fire('Error', 'Please upload a valid audio file (.mp3, .wav).', 'error');
                   }
                 }}
              >
                <input
                  ref={audioRef}
                  type="file"
                  accept="audio/*"
                  style={{ display: "none" }}
                  onChange={handleMusicUpload}
                />
                <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" className="upload-icon" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <p className="drag-drop-text">Drag & Drop your Audio file here</p>
                <p className="drag-drop-subtext">or click to browse</p>
                <p className="drag-drop-hint">Supported: .mp3, .wav files only</p>
              </div>
              {errors.audioUrl && <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.audioUrl}</span>}
            </>
          ) : (
            <div className="preview-section" style={{ marginBottom: "15px" }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <label style={{ margin: 0 }}>Audio Preview</label>
                <button 
                  type="button" 
                  onClick={() => {
                    setFormData(prev => ({ ...prev, audioUrl: null, duration: "" }));
                    if (audioRef.current) audioRef.current.value = "";
                  }} 
                  style={{ background: 'none', border: 'none', color: '#ef4444', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' }}
                >
                  Remove
                </button>
              </div>
              <audio
                controls
                className="audio-player"
                src={typeof formData.audioUrl === 'string' ? formData.audioUrl : URL.createObjectURL(formData.audioUrl)}
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Duration (Auto-calculated)</label>
          <input
            type="text"
            className="form-input"
            value={formData.duration}
            readOnly
            placeholder="0:00"
            style={{ background: "#e2e8f0" }}
          />
        </div>
        
        <div className="button-group" style={{ gridColumn: "2 / -1", margin: 0, paddingBottom: "2px", alignItems: "flex-end" }}>
          <button type="button" className="reset-btn" onClick={()=>{
            navigate("/music-listing")
          }}>
            Cancel
          </button>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? "Adding..." : "Add Music"}
          </button>
        </div>
      </form>

      <Modal show={showIconModal} onHide={() => setShowIconModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Icon</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(65px, 1fr))', gap: '8px', padding: '10px' }}>
            {MUSIC_ICONS.map((icon, idx) => (
              <div 
                key={idx}
                onClick={() => {
                  setFormData(prev => ({ ...prev, icon: icon }));
                  if (errors.icon) setErrors(prev => ({ ...prev, icon: "" }));
                  setShowIconModal(false);
                }}
                style={{ 
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
                  padding: '8px 4px', borderRadius: '8px', cursor: 'pointer',
                  border: formData.icon === icon ? '2px solid #6366f1' : '1px solid #e2e8f0',
                  backgroundColor: formData.icon === icon ? '#e0e7ff' : '#fff',
                  transition: 'all 0.2s'
                }}
              >
                <i className="material-icons" style={{ fontSize: '24px', color: formData.icon === icon ? '#6366f1' : '#64748b', marginBottom: '4px' }}>{icon}</i>
                <span style={{ fontSize: '10px', color: '#64748b', textAlign: 'center', wordBreak: 'break-all', lineHeight: '1.2' }}>{icon.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AddMusic;